import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, CheckCircle2, FileUp, Loader2, Plus, Save, Star, Trash2 } from 'lucide-react';
import { apiJson } from '@/lib/api';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useOptionalRetailerWorkspace } from '@/features/retailer-management/RetailerWorkspaceContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

type SlideRole = 'overview' | 'store_evidence' | 'appendix' | 'disabled';

interface ReportTemplate {
  id: number;
  name: string;
  version: string;
  sourceFileType: 'pptm' | 'pptx';
  isDefault: boolean;
  isArchived: boolean;
  validationMetadata?: {
    slideCount?: number;
    mediaCount?: number;
    issues?: Array<{ severity: string; message: string }>;
    profile?: ReportTemplateProfile;
  };
  createdAt: string;
}

interface ReportTemplateSlideProfile {
  slideNumber: number;
  part: string;
  layoutName: string | null;
  textRuns: string[];
  placeholders: Array<{ type: string | null; index: string | null }>;
  mediaRelationshipCount: number;
}

interface ReportTemplateProfile {
  profileVersion: 1;
  kind: 'pptx';
  slideSize: { cx: number; cy: number } | null;
  slides: ReportTemplateSlideProfile[];
  package: {
    slideCount: number;
    mediaCount: number;
    embeddedFontCount: number;
    hasVbaProject: boolean;
  };
}

interface TokenMapping {
  token: string;
  source: string;
}

interface SlideConfiguration {
  slideNumber: number;
  role: SlideRole;
  enabled: boolean;
  title: string;
  tokenMappings: TokenMapping[];
}

interface SopGuidance {
  title: string;
  processName: string;
  purpose: string;
  keyContacts: string[];
  campaignFormats: string[];
  notes: string[];
}

interface ReportStructure {
  id: number | null;
  structureJson: { slides: SlideConfiguration[]; sopGuidance?: SopGuidance };
  evidenceRulesJson: { imagesPerSlide: number; groupBy: 'store' | 'format'; sortBy: 'store_name' };
  captionRulesJson: { template: string };
}

const defaultDunnesSopGuidance: SopGuidance = {
  title: 'Standard Operating Procedure (SOP)',
  processName: 'Dunnes Campaign Reporting - Launch Document Process',
  purpose: 'This SOP outlines the standard process for preparing and delivering campaign reporting launch documents for Dunnes Stores retail media campaigns.',
  keyContacts: ['Ryan Reynolds', 'Lorraine Hogan'],
  campaignFormats: ['Branded Bay POS (most common)', '1 x floor graphic'],
  notes: ['For closed fridges, aisle fins will be applied as corner stickers rather than fins protruding into the aisle.'],
};

const tokenSourceLabels: Record<string, string> = {
  'campaign.name': 'Campaign name',
  'cycle.name': 'Cycle name',
  'cycle.startDate': 'Cycle start date',
  'cycle.endDate': 'Cycle end date',
  'retailer.name': 'Retailer name',
  'report.generatedDate': 'Report generated date',
  'sop.title': 'SOP title',
  'sop.processName': 'SOP process name',
  'sop.purpose': 'SOP purpose',
  'sop.keyContacts': 'SOP key contacts',
  'sop.campaignFormats': 'SOP campaign formats',
  'sop.notes': 'SOP notes',
  'store.title': 'Store slide title',
  'store.name': 'Store name',
  'store.number': 'Store number',
};

function roleLabel(role: SlideRole) {
  if (role === 'overview') return 'Campaign overview';
  if (role === 'store_evidence') return 'Store evidence';
  if (role === 'appendix') return 'Appendix';
  return 'Disabled';
}

function listToText(values?: string[]) {
  return (values ?? []).join('\n');
}

function textToList(value: string) {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function detectedTokens(slideProfile?: ReportTemplateSlideProfile, currentMappings: TokenMapping[] = []) {
  const tokens = new Set<string>();
  for (const text of slideProfile?.textRuns ?? []) {
    for (const match of text.matchAll(/\{[a-zA-Z0-9_.-]+\}/g)) tokens.add(match[0]);
    if (text.includes('Cycle XX')) tokens.add('Cycle XX');
    if (text.includes('XXXXXXXX')) tokens.add('XXXXXXXX');
    if (text.includes('XX.XX.XXXX')) tokens.add('XX.XX.XXXX');
  }
  for (const mapping of currentMappings) {
    if (mapping.token.trim()) tokens.add(mapping.token.trim());
  }
  return [...tokens];
}

function detectedTextPreview(slideProfile?: ReportTemplateSlideProfile) {
  const runs = slideProfile?.textRuns?.filter(Boolean) ?? [];
  if (runs.length === 0) return 'No visible text detected in this slide.';
  return runs.slice(0, 8).join(' · ');
}

export function ReportingTab({ retailerId, retailerName }: { retailerId: number; retailerName: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const workspace = useOptionalRetailerWorkspace();
  const [name, setName] = useState('Retail Media Compliance Report');
  const [version, setVersion] = useState('V03');
  const [file, setFile] = useState<File | null>(null);
  const [isDefault, setIsDefault] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [draftStructure, setDraftStructure] = useState<ReportStructure | null>(null);
  const [selectedSlideNumber, setSelectedSlideNumber] = useState<number | null>(null);
  const queryKey = ['/api/retailers', retailerId, 'reporting', 'templates'];
  const unsavedSourceId = `reporting:${retailerId}`;

  const templatesQuery = useQuery<ReportTemplate[]>({
    queryKey,
    queryFn: async () => {
      const response = await apiJson<{ success: boolean; data: { templates: ReportTemplate[] } }>(
        `/api/retailers/${retailerId}/reporting/templates`,
      );
      return response.data.templates;
    },
    retry: false,
  });

  const activeTemplates = useMemo(
    () => (templatesQuery.data ?? []).filter((template) => !template.isArchived),
    [templatesQuery.data],
  );
  const selectedTemplate = useMemo(
    () => (templatesQuery.data ?? []).find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templatesQuery.data],
  );
  const templateProfile = selectedTemplate?.validationMetadata?.profile;

  useEffect(() => {
    if (selectedTemplateId || activeTemplates.length === 0) return;
    setSelectedTemplateId(activeTemplates.find((template) => template.isDefault)?.id ?? activeTemplates[0].id);
  }, [activeTemplates, selectedTemplateId]);

  const structureQuery = useQuery<{ structure: ReportStructure; supportedTokenSources: string[] }>({
    queryKey: ['/api/retailers', retailerId, 'reporting', 'templates', selectedTemplateId, 'structure'],
    queryFn: async () => {
      const response = await apiJson<{ data: { structure: ReportStructure; supportedTokenSources: string[] } }>(
        `/api/retailers/${retailerId}/reporting/templates/${selectedTemplateId}/structure`,
      );
      return response.data;
    },
    enabled: selectedTemplateId != null,
    retry: false,
  });

  useEffect(() => {
    if (structureQuery.data?.structure) {
      setDraftStructure(JSON.parse(JSON.stringify(structureQuery.data.structure)));
    }
  }, [structureQuery.data]);

  useEffect(() => {
    const firstSlide = draftStructure?.structureJson.slides[0]?.slideNumber ?? null;
    if (!draftStructure || (selectedSlideNumber && draftStructure.structureJson.slides.some((slide) => slide.slideNumber === selectedSlideNumber))) return;
    setSelectedSlideNumber(firstSlide);
  }, [draftStructure, selectedSlideNumber]);

  const hasUnsavedReportChanges = useMemo(() => {
    const uploadFormDirty =
      !!file
      || name.trim() !== 'Retail Media Compliance Report'
      || version.trim() !== 'V03'
      || isDefault !== true;
    const structureDirty = !!(
      draftStructure
      && structureQuery.data?.structure
      && JSON.stringify(draftStructure) !== JSON.stringify(structureQuery.data.structure)
    );
    return uploadFormDirty || structureDirty;
  }, [draftStructure, file, isDefault, name, structureQuery.data?.structure, version]);

  const setUnsavedChanges = workspace?.setUnsavedChanges;

  useEffect(() => {
    if (!setUnsavedChanges) return;
    setUnsavedChanges(unsavedSourceId, hasUnsavedReportChanges);
    return () => setUnsavedChanges(unsavedSourceId, false);
  }, [hasUnsavedReportChanges, unsavedSourceId, setUnsavedChanges]);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a PowerPoint template first.');
      const form = new FormData();
      form.append('file', file);
      form.append('name', name);
      form.append('version', version);
      form.append('isDefault', String(isDefault));
      const response = await apiRequest(`/api/retailers/${retailerId}/reporting/templates`, 'POST', form);
      return response.json();
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey });
      const templateId = response?.data?.template?.id;
      if (templateId) setSelectedTemplateId(templateId);
      setFile(null);
      toast({ title: 'Template uploaded', description: 'The report template passed validation and now has a default structure.' });
    },
    onError: (error: any) => toast({
      title: 'Template upload failed',
      description: error?.data?.inspection?.issues?.map((issue: any) => issue.message).join(' ') || error.message,
      variant: 'destructive',
    }),
  });

  const setDefaultMutation = useMutation({
    mutationFn: (templateId: number) => apiRequest(
      `/api/retailers/${retailerId}/reporting/templates/${templateId}/default`, 'PUT',
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Default template updated' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (templateId: number) => apiRequest(
      `/api/retailers/${retailerId}/reporting/templates/${templateId}/archive`, 'PUT',
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSelectedTemplateId(null);
      toast({ title: 'Template archived' });
    },
  });

  const saveStructureMutation = useMutation({
    mutationFn: async () => {
      if (!draftStructure || !selectedTemplateId) throw new Error('No template structure is selected.');
      const response = await apiRequest(
        `/api/retailers/${retailerId}/reporting/templates/${selectedTemplateId}/structure`,
        'PUT',
        {
          structureJson: draftStructure.structureJson,
          evidenceRulesJson: draftStructure.evidenceRulesJson,
          captionRulesJson: draftStructure.captionRulesJson,
        },
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/retailers', retailerId, 'reporting', 'templates', selectedTemplateId, 'structure'] });
      toast({ title: 'Report structure saved', description: 'Future report generations will use these token mappings.' });
    },
    onError: (error: any) => toast({ title: 'Structure save failed', description: error.message, variant: 'destructive' }),
  });

  const updateSlide = (slideNumber: number, updates: Partial<SlideConfiguration>) => {
    setDraftStructure((current) => current ? {
      ...current,
      structureJson: {
        ...current.structureJson,
        slides: current.structureJson.slides.map((slide) => (
          slide.slideNumber === slideNumber ? { ...slide, ...updates } : slide
        )),
      },
    } : current);
  };

  const updateSlideMapping = (slideNumber: number, index: number, updates: Partial<TokenMapping>) => {
    setDraftStructure((current) => current ? {
      ...current,
      structureJson: {
        ...current.structureJson,
        slides: current.structureJson.slides.map((slide) => {
          if (slide.slideNumber !== slideNumber) return slide;
          return {
            ...slide,
            tokenMappings: slide.tokenMappings.map((mapping, mappingIndex) => (
              mappingIndex === index ? { ...mapping, ...updates } : mapping
            )),
          };
        }),
      },
    } : current);
  };

  const addSlideMapping = (slideNumber: number) => {
    setDraftStructure((current) => current ? {
      ...current,
      structureJson: {
        ...current.structureJson,
        slides: current.structureJson.slides.map((slide) => {
          if (slide.slideNumber !== slideNumber) return slide;
          return {
            ...slide,
            tokenMappings: [...slide.tokenMappings, { token: '', source: 'campaign.name' }],
          };
        }),
      },
    } : current);
  };

  const removeSlideMapping = (slideNumber: number, index: number) => {
    setDraftStructure((current) => current ? {
      ...current,
      structureJson: {
        ...current.structureJson,
        slides: current.structureJson.slides.map((slide) => {
          if (slide.slideNumber !== slideNumber) return slide;
          return {
            ...slide,
            tokenMappings: slide.tokenMappings.filter((_, mappingIndex) => mappingIndex !== index),
          };
        }),
      },
    } : current);
  };

  const updateSopGuidance = (updates: Partial<SopGuidance>) => {
    setDraftStructure((current) => current ? {
      ...current,
      structureJson: {
        ...current.structureJson,
        sopGuidance: {
          ...defaultDunnesSopGuidance,
          ...current.structureJson.sopGuidance,
          ...updates,
        },
      },
    } : current);
  };

  const supportedSources = structureQuery.data?.supportedTokenSources?.length
    ? structureQuery.data.supportedTokenSources
    : Object.keys(tokenSourceLabels);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Report configuration</CardTitle>
          <p className="text-sm text-muted-foreground">
            Manage templates and configure slide-level content injection for {retailerName} campaigns.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`report-name-${retailerId}`}>Template name</Label>
              <Input id={`report-name-${retailerId}`} value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`report-version-${retailerId}`}>Version</Label>
              <Input id={`report-version-${retailerId}`} value={version} onChange={(event) => setVersion(event.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`report-file-${retailerId}`}>PowerPoint template (.pptm or .pptx)</Label>
            <Input id={`report-file-${retailerId}`} type="file" accept=".pptm,.pptx" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id={`report-default-${retailerId}`} checked={isDefault} onCheckedChange={(checked) => setIsDefault(checked === true)} />
            <Label htmlFor={`report-default-${retailerId}`}>Make this the default template</Label>
          </div>
          <Button onClick={() => uploadMutation.mutate()} disabled={!file || !name.trim() || !version.trim() || uploadMutation.isPending}>
            {uploadMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}
            Upload and validate
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(420px,1.2fr)]">
        <Card>
          <CardHeader><CardTitle>Templates</CardTitle></CardHeader>
          <CardContent>
            {templatesQuery.isError && <Alert variant="destructive"><AlertTitle>Templates unavailable</AlertTitle><AlertDescription>{(templatesQuery.error as Error).message}</AlertDescription></Alert>}
            {!templatesQuery.isLoading && templatesQuery.data?.length === 0 && <p className="text-sm text-muted-foreground">No report templates have been uploaded.</p>}
            {!!templatesQuery.data?.length && (
              <Table>
                <TableHeader><TableRow><TableHead>Template</TableHead><TableHead>Validation</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {templatesQuery.data.map((template) => (
                    <TableRow key={template.id} className={template.isArchived ? 'opacity-60' : selectedTemplateId === template.id ? 'bg-muted/50' : ''}>
                      <TableCell>
                        <button type="button" className="text-left font-medium hover:underline" onClick={() => setSelectedTemplateId(template.id)}>{template.name}</button>
                        <div className="mt-1 flex flex-wrap gap-2">
                          <Badge variant="outline">{template.version}</Badge>
                          <Badge variant="secondary">{template.sourceFileType.toUpperCase()}</Badge>
                          {template.isDefault && <Badge><Star className="mr-1 h-3 w-3" />Default</Badge>}
                          {template.isArchived && <Badge variant="outline">Archived</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center text-sm text-emerald-700"><CheckCircle2 className="mr-1 h-4 w-4" />Valid</div>
                        <div className="text-xs text-muted-foreground">{template.validationMetadata?.slideCount ?? 2} source slides</div>
                        <div className="text-xs text-muted-foreground">
                          {template.validationMetadata?.profile ? 'Template profile available' : 'Profile available after re-upload'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        {!template.isDefault && !template.isArchived && <Button variant="outline" size="sm" onClick={() => setDefaultMutation.mutate(template.id)}>Set default</Button>}
                        {!template.isArchived && <Button variant="outline" size="sm" onClick={() => archiveMutation.mutate(template.id)}><Archive className="mr-1 h-4 w-4" />Archive</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Report structure</CardTitle>
            <p className="text-sm text-muted-foreground">
              Configure each template slide and map visible template tokens to app data. Use one mapping per line: <code>{'{campaign.name} = campaign.name'}</code>.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {!selectedTemplateId && <p className="text-sm text-muted-foreground">Select a template to configure slide structure.</p>}
            {structureQuery.isLoading && <div className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading structure…</div>}
            {structureQuery.isError && <Alert variant="destructive"><AlertTitle>Structure unavailable</AlertTitle><AlertDescription>{(structureQuery.error as Error).message}</AlertDescription></Alert>}
            {draftStructure && (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Images per evidence slide</Label>
                    <Select
                      value={String(draftStructure.evidenceRulesJson.imagesPerSlide)}
                      onValueChange={(value) => setDraftStructure({ ...draftStructure, evidenceRulesJson: { ...draftStructure.evidenceRulesJson, imagesPerSlide: Number(value) } })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{[1, 2, 3, 4].map((value) => <SelectItem key={value} value={String(value)}>{value}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Caption template</Label>
                    <Input
                      value={draftStructure.captionRulesJson.template}
                      onChange={(event) => setDraftStructure({ ...draftStructure, captionRulesJson: { template: event.target.value } })}
                    />
                  </div>
                </div>

                <div className="rounded-lg border p-4 space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">Template slide profile</div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        This is what the uploaded PowerPoint contains. Use it to decide what each slide does and which text tokens the app should replace.
                      </p>
                    </div>
                    {templateProfile && (
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">{templateProfile.package.slideCount} slides</Badge>
                        <Badge variant="outline">{templateProfile.package.mediaCount} media assets</Badge>
                        <Badge variant="outline">{templateProfile.package.embeddedFontCount} embedded fonts</Badge>
                      </div>
                    )}
                  </div>

                  {!templateProfile && (
                    <Alert>
                      <AlertTitle>No profile stored for this template yet</AlertTitle>
                      <AlertDescription>
                        Existing templates uploaded before the profile feature can still be configured, but detected slide text/placeholders will appear after the template is re-uploaded.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="grid gap-3 md:grid-cols-2">
                    {(templateProfile?.slides ?? draftStructure.structureJson.slides.map((slide) => ({
                      slideNumber: slide.slideNumber,
                      part: `slide${slide.slideNumber}.xml`,
                      layoutName: null,
                      textRuns: [],
                      placeholders: [],
                      mediaRelationshipCount: 0,
                    }))).map((slideProfile) => {
                      const configuredSlide = draftStructure.structureJson.slides.find((slide) => slide.slideNumber === slideProfile.slideNumber);
                      return (
                        <button
                          key={slideProfile.slideNumber}
                          type="button"
                          onClick={() => setSelectedSlideNumber(slideProfile.slideNumber)}
                          className={`rounded-md border p-3 text-left transition-colors ${selectedSlideNumber === slideProfile.slideNumber ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium">Slide {slideProfile.slideNumber}</div>
                            <Badge variant={configuredSlide?.enabled ? 'default' : 'outline'}>{configuredSlide?.enabled ? roleLabel(configuredSlide.role) : 'Disabled'}</Badge>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Layout: {slideProfile.layoutName ?? 'Unknown'} · Placeholders: {slideProfile.placeholders.length} · Media rels: {slideProfile.mediaRelationshipCount}
                          </div>
                          <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{detectedTextPreview(slideProfile)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-lg border p-4 space-y-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">SOP-driven report guidance</div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Store the Dunnes launch-document SOP alongside this template. These fields can drive slide copy via SOP token mappings.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => updateSopGuidance(defaultDunnesSopGuidance)}
                    >
                      Apply Dunnes SOP defaults
                    </Button>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>SOP title</Label>
                      <Input
                        value={draftStructure.structureJson.sopGuidance?.title ?? ''}
                        onChange={(event) => updateSopGuidance({ title: event.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Process name</Label>
                      <Input
                        value={draftStructure.structureJson.sopGuidance?.processName ?? ''}
                        onChange={(event) => updateSopGuidance({ processName: event.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Purpose</Label>
                    <Textarea
                      rows={3}
                      value={draftStructure.structureJson.sopGuidance?.purpose ?? ''}
                      onChange={(event) => updateSopGuidance({ purpose: event.target.value })}
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Key contacts</Label>
                      <Textarea
                        rows={4}
                        value={listToText(draftStructure.structureJson.sopGuidance?.keyContacts)}
                        onChange={(event) => updateSopGuidance({ keyContacts: textToList(event.target.value) })}
                      />
                      <p className="text-xs text-muted-foreground">One contact per line.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Campaign formats</Label>
                      <Textarea
                        rows={4}
                        value={listToText(draftStructure.structureJson.sopGuidance?.campaignFormats)}
                        onChange={(event) => updateSopGuidance({ campaignFormats: textToList(event.target.value) })}
                      />
                      <p className="text-xs text-muted-foreground">One format per line.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        rows={4}
                        value={listToText(draftStructure.structureJson.sopGuidance?.notes)}
                        onChange={(event) => updateSopGuidance({ notes: textToList(event.target.value) })}
                      />
                      <p className="text-xs text-muted-foreground">One note per line.</p>
                    </div>
                  </div>
                  <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
                    Useful template tokens: <code>{'{sop.title}'}</code>, <code>{'{sop.processName}'}</code>, <code>{'{sop.purpose}'}</code>, <code>{'{sop.keyContacts}'}</code>, <code>{'{sop.campaignFormats}'}</code>, <code>{'{sop.notes}'}</code>.
                  </div>
                </div>

                <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                  <div className="font-medium">Supported data sources</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {supportedSources.map((source) => <Badge key={source} variant="outline">{source}: {tokenSourceLabels[source] ?? source}</Badge>)}
                  </div>
                </div>

                <div className="space-y-4">
                  {draftStructure.structureJson.slides.map((slide) => (
                    <div
                      key={slide.slideNumber}
                      className={`rounded-lg border p-4 space-y-4 ${selectedSlideNumber === slide.slideNumber ? 'border-primary shadow-sm' : ''}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="font-medium">Slide {slide.slideNumber}: {slide.title || roleLabel(slide.role)}</div>
                        <div className="flex items-center gap-2">
                          <Checkbox checked={slide.enabled} onCheckedChange={(checked) => updateSlide(slide.slideNumber, { enabled: checked === true })} />
                          <span className="text-sm text-muted-foreground">Enabled</span>
                        </div>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Slide title</Label>
                          <Input value={slide.title} onChange={(event) => updateSlide(slide.slideNumber, { title: event.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Slide purpose</Label>
                          <Select value={slide.role} onValueChange={(value) => updateSlide(slide.slideNumber, { role: value as SlideRole })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="overview">Campaign overview</SelectItem>
                              <SelectItem value="store_evidence">Store evidence</SelectItem>
                              <SelectItem value="appendix">Appendix</SelectItem>
                              <SelectItem value="disabled">Disabled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                        <div className="rounded-md bg-muted/40 p-3 text-sm">
                          <div className="font-medium">Detected in template</div>
                          <div className="mt-2 space-y-2 text-xs text-muted-foreground">
                            {(() => {
                              const slideProfile = templateProfile?.slides.find((profileSlide) => profileSlide.slideNumber === slide.slideNumber);
                              const tokens = detectedTokens(slideProfile, slide.tokenMappings);
                              return (
                                <>
                                  <div><span className="font-medium text-foreground">Layout:</span> {slideProfile?.layoutName ?? 'Unknown'}</div>
                                  <div><span className="font-medium text-foreground">Text:</span> {detectedTextPreview(slideProfile)}</div>
                                  <div>
                                    <span className="font-medium text-foreground">Tokens:</span>{' '}
                                    {tokens.length > 0 ? tokens.map((token) => <Badge key={token} variant="outline" className="mr-1 mt-1">{token}</Badge>) : 'None detected'}
                                  </div>
                                  <div>
                                    <span className="font-medium text-foreground">Placeholders:</span>{' '}
                                    {(slideProfile?.placeholders.length ?? 0) > 0
                                      ? slideProfile?.placeholders.map((placeholder, index) => (
                                        <Badge key={`${placeholder.type}-${placeholder.index}-${index}`} variant="outline" className="mr-1 mt-1">
                                          {placeholder.type ?? 'placeholder'}{placeholder.index ? ` #${placeholder.index}` : ''}
                                        </Badge>
                                      ))
                                      : 'None detected'}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <Label>Token mappings</Label>
                              <p className="text-xs text-muted-foreground">
                                Overview mappings are active in generation. Evidence-slide mappings are saved here for renderer expansion.
                              </p>
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={() => addSlideMapping(slide.slideNumber)}>
                              <Plus className="mr-1 h-4 w-4" />Add mapping
                            </Button>
                          </div>
                          {slide.tokenMappings.length === 0 && (
                            <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                              No token mappings configured for this slide.
                            </div>
                          )}
                          {slide.tokenMappings.map((mapping, index) => {
                            const slideProfile = templateProfile?.slides.find((profileSlide) => profileSlide.slideNumber === slide.slideNumber);
                            const tokenOptions = detectedTokens(slideProfile, slide.tokenMappings);
                            return (
                              <div key={`${slide.slideNumber}-${index}`} className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                                <Input
                                  list={`report-slide-${slide.slideNumber}-tokens`}
                                  value={mapping.token}
                                  placeholder="{campaign.name}"
                                  onChange={(event) => updateSlideMapping(slide.slideNumber, index, { token: event.target.value })}
                                />
                                <datalist id={`report-slide-${slide.slideNumber}-tokens`}>
                                  {tokenOptions.map((token) => <option key={token} value={token} />)}
                                </datalist>
                                <Select value={mapping.source} onValueChange={(value) => updateSlideMapping(slide.slideNumber, index, { source: value })}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {supportedSources.map((source) => (
                                      <SelectItem key={source} value={source}>{tokenSourceLabels[source] ?? source}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeSlideMapping(slide.slideNumber, index)} aria-label="Remove token mapping">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button onClick={() => saveStructureMutation.mutate()} disabled={saveStructureMutation.isPending}>
                  {saveStructureMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save report structure
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
